// src/components/Sidebar.jsx
import { NavLink } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext.jsx';

const ADMIN_ROLES = ['super_admin', 'project_admin', 'clinical_supervisor', 'ict_admin'];

const SECTIONS = [
  {
    label: 'Overview',
    items: [
      { to: '/', label: 'Queue', icon: '📋', end: true },
      { to: '/mine', label: 'My cases', icon: '🗂️' },
    ],
  },
  {
    label: 'Cases',
    items: [
      { to: '/cases/mental-health', label: 'Mental Health', icon: '🧠' },
      { to: '/cases/gbv', label: 'GBV Cases', icon: '🛡️' },
      { to: '/cases/follow-up', label: 'Follow-up', icon: '📅' },
      { to: '/cases/live', label: 'Live Conversations', icon: '💬', comingSoon: true },
    ],
  },
  {
    label: 'Planning',
    items: [
      { to: '/appointments', label: 'Appointments', icon: '🗓️', comingSoon: true },
    ],
  },
  {
    label: 'Insights',
    items: [
      { to: '/reports', label: 'Reports & Analytics', icon: '📊', adminOnly: true },
    ],
  },
  {
    label: 'Administration',
    items: [
      { to: '/staff', label: 'Staff', icon: '👥', adminOnly: true },
      { to: '/roles', label: 'Roles & Permissions', icon: '🔐', adminOnly: true },
      { to: '/resources', label: 'Resource Directory', icon: '📚', adminOnly: true },
      { to: '/audit-log', label: 'Audit Logs', icon: '🕵️', adminOnly: true },
      { to: '/settings', label: 'Settings', icon: '⚙️', adminOnly: true },
    ],
  },
];

export default function Sidebar() {
  const { staff, logout } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(staff?.role);

  return (
    <aside className="w-64 shrink-0 bg-ink text-white flex flex-col h-screen sticky top-0 overflow-y-auto">
      <div className="px-5 py-6">
        <div className="font-semibold text-lg tracking-tight">Sauti</div>
        <div className="text-xs text-white/50 mt-0.5">Caritas of Hope</div>
      </div>
      <nav className="flex-1 px-3 space-y-5 pb-4">
        {SECTIONS.map((section) => {
          const items = section.items.filter((item) => !item.adminOnly || isAdmin);
          if (items.length === 0) return null;
          return (
            <div key={section.label}>
              <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/35">
                {section.label}
              </div>
              <div className="space-y-0.5">
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.comingSoon ? '#' : item.to}
                    end={item.end}
                    onClick={(e) => item.comingSoon && e.preventDefault()}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                        item.comingSoon
                          ? 'text-white/30 cursor-default'
                          : isActive
                          ? 'bg-white/10 text-white'
                          : 'text-white/70 hover:bg-white/5 hover:text-white'
                      }`
                    }
                  >
                    <span className="text-sm">{item.icon}</span>
                    <span className="flex-1">{item.label}</span>
                    {item.comingSoon && <span className="text-[9px] uppercase tracking-wide bg-white/10 rounded px-1.5 py-0.5">Soon</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="px-4 py-4 border-t border-white/10">
        <div className="text-sm font-medium truncate">{staff?.name}</div>
        <div className="text-xs text-white/50 capitalize">{staff?.role?.replace('_', ' ')}</div>
        <button
          onClick={logout}
          className="mt-3 text-xs text-white/60 hover:text-white underline underline-offset-2"
        >
          Sign out
        </button>
      </div>
    </aside>
  );
}
